import { useCallback, useMemo } from "react";
import { EventType } from "globals/types/models.ts";
import { getUrlFromFormat } from "globals/functions/image.ts";
import { useSingleEventData } from "utils/data/event.ts";
import { useTranslation } from "react-i18next";
import { LANGS } from "globals/constants.ts";
import { useAppSelector } from "config/store";
import Button from "components/atom/button/Button.tsx";
import CategoryIcon from "components/atom/CategoryIcon.tsx";
import EventDateListItem from "components/layouts/event/EventDateListItem.tsx";
import Icon from "components/atom/Icon.tsx";

function PublicEventCard(props: {
  event: EventType,
  onClick: (event: EventType) => void
}) {
  const { event, onClick } = props;

  const { t } = useTranslation();
  const currentLang = useAppSelector(state => state.global.currentLang);

  const {
    category,
    upcomingDate,
    pastDate,
    shortAddress
  } = useSingleEventData(event);

  const localOnClick = useCallback(() => {
    onClick(event);
  }, [onClick, event]);

  const imageUrl = useMemo(() => {
    const firstImage = event.images.length > 0 ? event.images[0] : null;
    let imageUrl = firstImage ? firstImage.imageUrl : null;
    if (firstImage && imageUrl) {
      if (firstImage.formats) {
        if (firstImage.formats.length > 0) {
          imageUrl = getUrlFromFormat(imageUrl, firstImage.formats, 512);//124px
        }
      }
    }
    return imageUrl;
  }, [event]);


  const eventTitle = currentLang === LANGS.FR ? event.titleFr : event.titleEn ? event.titleEn : event.titleFr;

  return (
    <div
      className={`w-full cursor-pointer relative rounded-md p-2 flex flex-col justify-start items-start gap-2 border border-green-500 bg-green-150`}
    >
      <div className="h-[184px] w-full overflow-hidden rounded-md">
        {imageUrl ? (
          <img src={imageUrl} className="block h-full w-full object-cover object-center" />
        ) : (
          <div className="flex bg-gray-100 h-full w-full flex-col items-center justify-center">
            <span className="text-center text-xxs text-gray-400">
              {t("forms.noImage")}
            </span>
          </div>
        )}
      </div>
      <div className="flex-1 w-full flex flex-col justify-start items-start gap-1 bg-white p-4 rounded-md mt-4">
        <div className="flex flex-row items-center justify-start gap-4">
          {category && (
            <div
              className="flex h-10 w-10 min-w-10 flex-col items-center justify-center rounded-full bg-green-150 p-2.5">
              <CategoryIcon handle={category.handle}
                            className="block h-full w-full text-green-500" />
              <span className="sr-only">
                {category.titleFr}
              </span>
            </div>
          )}
          <div className="sm:mr-2">
            <h1 className="text-lg font-semibold">
              {eventTitle}
            </h1>
            {pastDate && (
              <h3 className="text-xs font-medium text-red-400">
                {t("event.pastEvent")}
              </h3>
            )}
          </div>
        </div>

        <div>
          <ul className="space-y-1 pt-2">
            {upcomingDate ? (
              <EventDateListItem hideIconLabel date={upcomingDate} />
            ) : (
              <span>
                {pastDate && (
                  <EventDateListItem hideIconLabel date={pastDate} />
                )}
              </span>
            )}
            {shortAddress && (
              <li className="w-full flex flex-row items-center justify-start gap-1">
                <dl
                  className="w-full flex flex-row items-center justify-start gap-2"
                >
                  <dt
                    className="flex flex-row items-center justify-start gap-1 text-base text-gray-400 font-normal">
                    <Icon name="map-pin" className="h-4 w-4 min-w-4 text-gray-400" />
                  </dt>
                  <dd className="text-base text-gray-600 flex-1">
                    <p className="text-base text-gray-600">
                      {shortAddress}
                    </p>
                  </dd>
                </dl>
              </li>
            )}
            {event.price && (
              <li className="w-full flex flex-row items-center justify-start gap-1">
                <dl
                  className="w-full flex flex-row items-center justify-start gap-2"
                >
                  <dt
                    className="flex flex-row items-center justify-start gap-1 text-base text-gray-400 font-normal">
                    <Icon name="dollar-sign" className="h-4 w-4 min-w-4 text-gray-400" />
                  </dt>
                  <dd className="text-base text-gray-600 flex-1">
                    {event.price}
                  </dd>
                </dl>
              </li>
            )}
          </ul>
        </div>
        <div className="w-full mt-auto">
          <Button className="w-full mt-4" onClick={localOnClick}>
            {t("event.seeMore")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PublicEventCard;
